/**
 * BACKOFFICE LOGISTIQUE — Réception / pesée / surcharge / préparation (#662).
 * Méthodes extraites telles quelles du god-service `backoffice.service.ts`
 * (workstream C) : scan DPD, enregistrement réception, station de pesée,
 * écarts de poids, préparation & tri destination, incidents entrepôt.
 */

import { Injectable } from '@angular/core';
import { uploadTimeoutMs } from '../../core/utils/interrupted-call';
import { Firestore, collection, query, where, getDocs, doc, updateDoc, getDoc, getDocFromServer, limit } from '@angular/fire/firestore';
import { Functions, httpsCallable, HttpsCallableOptions } from '@angular/fire/functions';
import {
  ShipmentListItem,
  ReceptionSearchResult,
  WeighResult
} from '../models/backoffice.models';
import { compareByCreatedAtDesc, dpdCodeCandidates, mapToListItem, mapToReceptionResult } from './backoffice-mapper';
import { GroupageScanCandidate } from '../utils/groupage-scan-refusal';
import { BackofficeActivityService } from './backoffice-activity.service';
import { isVmOrderUnsettled } from '../../core/models/vm-status.constants';
// #1804 — la cible de « Terminer préparation » est déclarée UNE fois, à côté des
// ensembles du portillon, pour que l'écran annonce le refus sur la même cible.
import { COMPLETE_PREPARATION_TARGET_STATUS } from '../../core/models/weight-surcharge.constants';

/**
 * #1349 — un colis VM dont la commande client n'est pas soldée n'entre pas dans
 * la file de préparation : il reste en réception VM jusqu'au paiement.
 *
 * Jugé sur la COMMANDE (`virtualMailboxData.shippingRequest.status`) et non sur
 * `status.current` ni sur la phase : la phase d'un colis VM converge vers
 * `warehouse` et le statut d'attente est un miroir écrasable
 * (cf. `core/models/vm-status.constants.ts`). Un colis VM payé (`paid`), annulé
 * (`cancelled`) ou sans commande n'est jamais écarté — la régression symétrique
 * (bloquer un colis légitime) est plus grave que le défaut d'origine.
 *
 * ⚠️ Ce prédicat est CELUI DU SERVEUR (`isVmOrderUnsettled` = « Befret a-t-il
 * été payé ? »), pas celui du filtre « Attente paiement VM » de la liste
 * (`isVmOrderAwaitingPayment` = « le client doit-il payer ? »). Un devis expiré
 * n'a plus rien de payable et sort donc de la vue d'attente, mais il n'a rien
 * encaissé : il ne doit surtout pas entrer en préparation. Aligner les deux
 * ferait proposer à l'agent une action que la CF refusera.
 *
 * ⚠️ Comme `PENDING_DISCREPANCY_STATUSES`, ce filtre ne protège que
 * l'AFFICHAGE. Le vrai verrou est serveur : `unpaidVmOrderBlockReason` dans
 * `functions/src/domains/shipment/payment-gates.ts` (#1394 — appliqué par les
 * DEUX writers de statut du backoffice, `transitionShipmentStatus` et
 * `updateShipmentStatusManual`, qui le contournait).
 */
export function isBlockedByUnpaidVmOrder(item: ShipmentListItem): boolean {
  return isVmOrderUnsettled(item.vmRequestStatus);
}

/** Motifs d'incident de préparation (blocage du colis). */
export type IncidentType = 'empty' | 'dangerous' | 'damaged' | 'payment_pending';

/**
 * #2161 — emballage d'un colis avant l'impression de son étiquette : emballé par
 * Befret, ou déjà bien emballé à l'arrivée (demande de Yannick Nseka, 28/10).
 */
export type PackagingChoice = 'packed_by_befret' | 'already_well_packed';

@Injectable({ providedIn: 'root' })
export class ReceptionService {

  constructor(
    private firestore: Firestore,
    private functions: Functions,
    private activity: BackofficeActivityService
  ) {}

  // ── Reception Search ──

  async searchByDpdCode(code: string): Promise<ReceptionSearchResult | null> {
    const trimmed = code.trim();
    if (!trimmed) return null;

    const shipmentsRef = collection(this.firestore, 'shipments');

    // 1./2. Champs DPD — essayer chaque candidat normalisé (#416)
    let snap = null as Awaited<ReturnType<typeof getDocs>> | null;
    for (const candidate of dpdCodeCandidates(trimmed)) {
      let q = query(shipmentsRef, where('standardData.dpdTrackingNumber', '==', candidate), limit(1));
      snap = await getDocs(q);
      if (!snap.empty) break;

      q = query(shipmentsRef, where('dpdIntegration.trackingNumber', '==', candidate), limit(1));
      snap = await getDocs(q);
      if (!snap.empty) break;
    }

    // 3. Try BF tracking number
    if (!snap || snap.empty) {
      const q = query(shipmentsRef, where('trackingNumber', '==', trimmed.toUpperCase()), limit(1));
      snap = await getDocs(q);
    }

    if (snap.empty) return null;

    const docSnap = snap.docs[0];
    return mapToReceptionResult(docSnap);
  }

  // ── Reception Actions ──

  async registerReception(
    shipmentId: string,
    trackingNumber: string,
    senderInfo: { name: string; email: string; phone: string }
  ): Promise<void> {
    // #399/#403 — CF de transition : passage received_at_warehouse + stamps
    // (warehouse.receivedAt, timestamps.warehouseAt, befretIntegration.warehouseArrival)
    // atomiques, gate paiement serveur et `status.phase` (sortie du scope
    // dpd-status-sync). La notification entrepôt est portée par le trigger
    // `statusNotificationDispatchTrigger` (clé partagée `warehouseArrival`,
    // revendication transactionnelle) — plus rien à envoyer/marquer côté client.
    // #416 — captureLocation() retiré du chemin du scan : le timeout géoloc (5 s)
    // ajoutait de la latence à CHAQUE scan de réception alors que la réception a
    // lieu à l'entrepôt Befret (position fixe et connue). Les actions terrain
    // mobiles (livraison RDC…) conservent leur capture de position.
    const fn = httpsCallable<any, any>(this.functions, 'transitionShipmentStatus');
    await fn({
      shipmentId,
      targetStatus: 'received_at_warehouse',
      details: { registeredBy: 'warehouse_staff' },
    });
  }

  // ── Weigh Station ──

  /**
   * Pesée entrepôt via CF `recordBefretWeight` (#403) : bornes, poids déclaré et
   * seuil financier (>0,5 kg) sont recalculés SERVEUR — le résultat retourné est
   * la source de vérité (le calcul local ne sert qu'à la prévisualisation).
   * Si écart significatif (hasFinancialImpact), la suite est gérée par
   * calculateWeightSurcharge() (montant réel + lien de paiement / avoir).
   */
  async saveBefretWeight(shipmentId: string, befretWeight: number): Promise<WeighResult> {
    const fn = httpsCallable<any, any>(this.functions, 'recordBefretWeight');
    const res: any = await fn({ shipmentId, befretWeight });
    const d = res?.data || {};
    return {
      declaredWeight: d.declaredWeight ?? 0,
      befretWeight: d.befretWeight ?? befretWeight,
      delta: d.delta ?? 0,
      deltaPercentage: d.deltaPercentage ?? 0,
      hasFinancialImpact: !!d.hasFinancialImpact,
    };
  }

  // ── Weight Discrepancy Callables ──

  async calculateWeightSurcharge(shipmentId: string, befretWeight: number): Promise<any> {
    const fn = httpsCallable<any, any>(this.functions, 'calculateWeightSurcharge');
    const result = await fn({ shipmentId, befretWeight });
    return result.data;
  }

  async createWeightSurchargePaymentLink(shipmentId: string, photo?: string | null): Promise<any> {
    const fn = httpsCallable<any, any>(this.functions, 'createWeightSurchargePaymentLink');
    const result = await fn({ shipmentId, photo: photo || undefined });
    return result.data;
  }

  async createWeightCreditPromoCode(shipmentId: string): Promise<any> {
    const fn = httpsCallable<any, any>(this.functions, 'createWeightCreditPromoCode');
    const result = await fn({ shipmentId });
    return result.data;
  }

  async resolveWeightDiscrepancy(shipmentId: string, resolution: 'waive' | 'cancel', notes?: string): Promise<any> {
    const fn = httpsCallable<any, any>(this.functions, 'resolveWeightDiscrepancy');
    const result = await fn({ shipmentId, resolution, notes });
    return result.data;
  }

  // ── Photo Upload ──

  /**
   * Couture de test (#1992) — `httpsCallable` est une fonction de module ESM,
   * non espionnable sous Karma (même motif que `vm-process-package`, #987).
   */
  protected callable(name: string, options?: HttpsCallableOptions): (data: unknown) => Promise<{ data: any }> {
    return httpsCallable(this.functions, name, options) as (data: unknown) => Promise<{ data: any }>;
  }

  async uploadPhoto(base64: string, folder: string, filename: string): Promise<string> {
    // #1992 — délai explicite, proportionnel à la taille : sans lui, une requête
    // perdue laissait l'écran attendre les 70 s du SDK.
    const fn = this.callable('uploadBackofficePhoto', { timeout: uploadTimeoutMs(base64.length) });
    const result = await fn({ base64, folder, filename });
    if (!result.data.success) throw new Error(result.data.error || 'Upload failed');
    return result.data.url;
  }

  // ── Warehouse Lists (for reception dashboard) ──

  async getShipmentsForWeighing(): Promise<ShipmentListItem[]> {
    const shipmentsRef = collection(this.firestore, 'shipments');
    const q = query(shipmentsRef, where('currentPhase', '==', 'warehouse'), limit(200));
    const snap = await getDocs(q);

    return snap.docs
      .map(d => mapToListItem(d))
      .filter(item => !item.befretWeight || item.befretWeight <= 0)
      .sort(compareByCreatedAtDesc);
  }

  async getRecentReceptions(daysBack = 1): Promise<ShipmentListItem[]> {
    const shipmentsRef = collection(this.firestore, 'shipments');
    const q = query(shipmentsRef, where('currentPhase', '==', 'warehouse'), limit(200));
    const snap = await getDocs(q);

    const cutoff = new Date();
    cutoff.setDate(cutoff.getDate() - daysBack);
    cutoff.setHours(0, 0, 0, 0);

    return snap.docs
      .map(d => {
        const item = mapToListItem(d);
        // Use warehouse arrival date for filtering, fallback to createdAt
        const data = d.data();
        const receivedAt = data['befretIntegration']?.['warehouseArrival']?.toDate?.()
          || data['warehouse']?.['receivedAt']?.toDate?.()
          || item.createdAt;
        (item as any)._receivedAt = receivedAt;
        return item;
      })
      .filter(item => (item as any)._receivedAt >= cutoff)
      .sort((a, b) => ((b as any)._receivedAt?.getTime() || 0) - ((a as any)._receivedAt?.getTime() || 0));
  }

  // ── Préparation ──

  // Statuts écart de poids bloquants (en attente de résolution)
  //
  // #1061 (constat 4) — `reminder_3_sent` et `return_to_sender` manquaient :
  // un colis relancé TROIS fois sans paiement (le plus mauvais payeur du lot)
  // redevenait préparable et disparaissait du worklist « écarts en attente ».
  // Il sortait donc de l'entrepôt sans que la dette soit recouvrée, exactement
  // à l'étape où le processus de relance venait d'échouer.
  //
  // ⚠️ Cette liste ne protège que l'AFFICHAGE. Le vrai verrou est serveur :
  // le gate `unpaidSurchargeBlockReason`
  // (functions/src/domains/shipment/payment-gates.ts, #1394 — appliqué par les
  // deux writers de statut du backoffice). Garder les deux listes cohérentes —
  // l'écran ne doit pas proposer une action que le serveur refusera.
  private readonly PENDING_DISCREPANCY_STATUSES = [
    'pending_payment', 'credit_pending', 'payment_link_sent',
    'reminder_1_sent', 'reminder_2_sent', 'reminder_3_sent',
    'return_to_sender'
  ];

  async getShipmentsForPreparation(limitCount = 200): Promise<ShipmentListItem[]> {
    const shipmentsRef = collection(this.firestore, 'shipments');
    const q = query(shipmentsRef, where('currentPhase', '==', 'warehouse'), limit(limitCount));
    const snap = await getDocs(q);

    return snap.docs
      .map(d => mapToListItem(d))
      .filter(item => item.befretWeight && item.befretWeight > 0)
      // Exclure les colis avec un écart de poids en attente de résolution
      .filter(item => !this.PENDING_DISCREPANCY_STATUSES.includes(item.weightDiscrepancyStatus || ''))
      // #1349 — exclure les colis VM dont la commande client n'est pas payée
      .filter(item => !isBlockedByUnpaidVmOrder(item))
      // Exclure les colis déjà préparés (warehouse_sorted, destination_sorted)
      .filter(item => item.statusCurrent !== 'warehouse_sorted' && item.statusCurrent !== 'destination_sorted')
      .sort(compareByCreatedAtDesc);
  }

  async getShipmentsWithPendingDiscrepancy(): Promise<ShipmentListItem[]> {
    const shipmentsRef = collection(this.firestore, 'shipments');
    const q = query(shipmentsRef, where('currentPhase', '==', 'warehouse'), limit(200));
    const snap = await getDocs(q);

    return snap.docs
      .map(d => mapToListItem(d))
      .filter(item => this.PENDING_DISCREPANCY_STATUSES.includes(item.weightDiscrepancyStatus || ''))
      .sort(compareByCreatedAtDesc);
  }

  // ── Tri par destination ──

  async getShipmentsForDestinationSorting(limitCount = 200): Promise<ShipmentListItem[]> {
    const shipmentsRef = collection(this.firestore, 'shipments');
    const q = query(shipmentsRef, where('currentPhase', '==', 'warehouse'), limit(limitCount));
    const snap = await getDocs(q);
    return snap.docs
      .filter(d => d.data()['status']?.['current'] === 'warehouse_sorted')
      .map(d => mapToListItem(d))
      .sort(compareByCreatedAtDesc);
  }

  async confirmDestinationSorting(shipmentId: string): Promise<void> {
    // #399 (PR A pilote) — passe par la CF de transition (matrice + auth par
    // transition + écriture atomique) au lieu d'un updateDoc direct. La CF pose
    // status.current/label, currentPhase si besoin, l'activity event et le stamp
    // `preparation.destinationSortedAt`. Notifications aval : trigger existant.
    const location = await this.activity.captureLocation();
    const fn = httpsCallable<any, any>(this.functions, 'transitionShipmentStatus');
    await fn({
      shipmentId,
      targetStatus: 'destination_sorted',
      ...(location && { location }),
    });
  }

  // ── Classement ──

  async getShipmentsForClassification(limitCount = 500): Promise<ShipmentListItem[]> {
    return (await this.getShipmentsForClassificationPage(limitCount)).items;
  }

  /**
   * #2161 — même lecture, et dit si le plafond a été atteint : la requête n'a pas
   * de tri, donc au-delà de `limitCount` colis en phase `warehouse`, un colis trié
   * peut manquer à la liste sans que rien ne le signale. Le groupage l'annonce.
   */
  async getShipmentsForClassificationPage(limitCount = 500): Promise<{ items: ShipmentListItem[]; truncated: boolean }> {
    const shipmentsRef = collection(this.firestore, 'shipments');
    const q = query(shipmentsRef, where('currentPhase', '==', 'warehouse'), limit(limitCount));
    const snap = await getDocs(q);
    const items = snap.docs
      .filter(d => {
        const data = d.data();
        return data['status']?.['current'] === 'destination_sorted'
          && !data['expedition']?.['containerId'];
      })
      .map(d => mapToListItem(d))
      .sort(compareByCreatedAtDesc);
    return { items, truncated: snap.size >= limitCount };
  }

  /**
   * #2161 — un code scanné au groupage qui n'est pas dans la liste : relit le colis
   * (numéro Befret, puis numéro DPD) pour dire POURQUOI il n'y est pas.
   */
  async findGroupageScanCandidate(code: string): Promise<GroupageScanCandidate | null> {
    const shipmentsRef = collection(this.firestore, 'shipments');
    const normalized = code.trim().toUpperCase();
    for (const field of ['trackingNumber', 'standardData.dpdTrackingNumber']) {
      const snap = await getDocs(query(shipmentsRef, where(field, '==', normalized), limit(1)));
      if (snap.empty) continue;
      const data = snap.docs[0].data();
      return {
        trackingNumber: data['trackingNumber'] || normalized,
        statusCurrent: data['status']?.['current'] || '',
        containerNumber: data['expedition']?.['containerNumber'] || data['expedition']?.['containerId'] || null,
      };
    }
    return null;
  }

  /**
   * #1992 — relecture pour « Vérifier » : TOUJOURS au serveur. Une lecture qui
   * pourrait venir du cache local rendrait, hors ligne, l'état d'avant l'action
   * — lu à tort comme « rien n'a été enregistré ». Hors ligne, elle échoue.
   */
  async getShipmentFromServer(shipmentId: string): Promise<any> {
    const snap = await getDocFromServer(doc(this.firestore, 'shipments', shipmentId));
    if (!snap.exists()) return null;
    return { id: snap.id, ...snap.data() };
  }

  async getShipmentForPreparation(shipmentId: string): Promise<any> {
    const docRef = doc(this.firestore, 'shipments', shipmentId);
    const snap = await getDoc(docRef);
    if (!snap.exists()) return null;
    return { id: snap.id, ...snap.data() };
  }

  async savePreparationVerification(
    shipmentId: string,
    data: { condition: string; conditionPhotos?: string[]; conditionNotes?: string; verifiedBy?: string }
  ): Promise<void> {
    const shipmentRef = doc(this.firestore, 'shipments', shipmentId);
    const now = new Date();
    const location = await this.activity.captureLocation();
    const updateData: any = {
      'preparation.verifiedAt': now,
      'preparation.verifiedBy': data.verifiedBy || 'warehouse_staff',
      'preparation.condition': data.condition,
      'timestamps.updatedAt': now,
      ...this.activity.buildActivityEvent('package_verified', `Colis vérifié — état: ${data.condition}`, 'warehouse', 'backoffice_preparation', { condition: data.condition, ...(location && { location }) })
    };
    if (data.conditionPhotos?.length) updateData['preparation.conditionPhotos'] = data.conditionPhotos;
    if (data.conditionNotes) updateData['preparation.conditionNotes'] = data.conditionNotes;
    await updateDoc(shipmentRef, updateData);
  }

  async saveBefretLabel(
    shipmentId: string,
    labelUrl: string,
    transportMode: string,
    destinationZone: string,
    packagingChoice?: PackagingChoice | null,
  ): Promise<void> {
    const shipmentRef = doc(this.firestore, 'shipments', shipmentId);
    await updateDoc(shipmentRef, {
      'preparation.befretLabelUrl': labelUrl,
      'preparation.transportMode': transportMode,
      'preparation.destinationZone': destinationZone,
      // #2161 — choix fait dans la fenêtre d'emballage, avant l'impression.
      ...(packagingChoice ? { 'preparation.packagingChoice': packagingChoice, 'preparation.packagingChoiceAt': new Date() } : {}),
      'timestamps.updatedAt': new Date(),
    });
  }

  async savePackagingPhoto(shipmentId: string, photoUrl: string): Promise<void> {
    const shipmentRef = doc(this.firestore, 'shipments', shipmentId);
    const location = await this.activity.captureLocation();
    await updateDoc(shipmentRef, {
      'preparation.packagingPhotoUrl': photoUrl,
      'preparation.packagingPhotoAt': new Date(),
      'timestamps.updatedAt': new Date(),
      ...this.activity.buildActivityEvent('packaging_photo', 'Photo d\'emballage ajoutée', 'warehouse', 'backoffice_preparation', { photoUrl, ...(location && { location }) })
    });
  }

  async flagShipmentIncident(
    shipmentId: string,
    trackingNumber: string,
    incident: IncidentType,
    senderInfo: { name: string; email: string; phone: string },
    conditionPhotos: string[] = [],
    notes?: string
  ): Promise<void> {
    const location = await this.activity.captureLocation();

    const incidentLabels: Record<string, string> = {
      'empty': 'Colis vide',
      'dangerous': 'Dangereux/Suspect',
      'damaged': 'Endommagé',
      'payment_pending': 'En attente de paiement',
    };
    const label = incidentLabels[incident] || incident;

    // 1. #399 — CF de transition : preparation_blocked + champs preparation.*
    // (incident) atomiques et validés serveur. Libellé dynamique via labelOverride.
    const fn = httpsCallable<any, any>(this.functions, 'transitionShipmentStatus');
    await fn({
      shipmentId,
      targetStatus: 'preparation_blocked',
      labelOverride: `Bloqué — ${label}`,
      incident: { type: incident, notes: notes || '', conditionPhotos },
      details: { incident, notes: notes || '', ...(conditionPhotos.length > 0 && { photoCount: conditionPhotos.length }) },
      ...(location && { location }),
    });

    // 2. Send notification (traced in timeline)
    this.sendIncidentNotification(trackingNumber, incident, senderInfo, notes, shipmentId)
      .catch(err => console.warn('Notification incident échouée:', err));
  }

  /**
   * Prévient l'expéditeur d'un incident de préparation. Envoyée APRÈS la
   * transition : si la réponse du serveur est perdue (coupure, #1992), l'écran
   * la rappelle une fois l'incident constaté par « Vérifier ».
   */
  sendIncidentNotification(
    trackingNumber: string,
    incident: IncidentType,
    senderInfo: { name: string; email: string; phone: string },
    notes: string | undefined,
    shipmentId: string,
  ): Promise<unknown> {
    const notificationTypes: Record<IncidentType, string> = {
      'empty': 'empty_parcel',
      'dangerous': 'dangerous_goods',
      'damaged': 'damaged',
      'payment_pending': 'payment_pending_surcharge',
    };
    return this.activity.sendNotification({
      trackingNumber,
      type: notificationTypes[incident] || 'damaged',
      recipientEmail: senderInfo.email,
      recipientPhone: senderInfo.phone,
      language: 'fr',
      channels: ['email', 'sms'],
      data: { recipientName: senderInfo.name, incident, notes: notes || '' }
    }, shipmentId);
  }

  async resolveShipmentIncident(shipmentId: string, resolution: string): Promise<void> {
    // #399 — CF de transition : preparation_blocked → received_at_warehouse
    // + champs preparation.incidentResolved*. Libellé dynamique via labelOverride.
    const location = await this.activity.captureLocation();
    const fn = httpsCallable<any, any>(this.functions, 'transitionShipmentStatus');
    await fn({
      shipmentId,
      targetStatus: 'received_at_warehouse',
      labelOverride: 'Incident résolu — en attente de préparation',
      resolution: { resolution },
      details: { resolution },
      ...(location && { location }),
    });
  }

  async completePreparation(
    shipmentId: string,
    trackingNumber: string,
    senderInfo: { name: string; email: string; phone: string }
  ): Promise<boolean> {
    // #399 (PR B) — CF de transition (matrice + auth + atomicité). La CF pose
    // status.current/label, le stamp `preparation.sortedAt` et l'activity event.
    // #2164 lot 11 — rend `true` quand la CF n'a rien fait (colis déjà au statut
    // cible, réponse `noop`) : l'écran ne l'annonce pas comme une réussite.
    const location = await this.activity.captureLocation();
    const fn = httpsCallable<any, any>(this.functions, 'transitionShipmentStatus');
    const result = await fn({
      shipmentId,
      targetStatus: COMPLETE_PREPARATION_TARGET_STATUS,
      details: { completedBy: 'warehouse_staff' },
      ...(location && { location }),
    });
    return result?.data?.noop === true;
  }
}
